// ============================================================================
// @torre/expenses — Historial de pagos de plataformas (gráficas de Pago de plataformas)
// ============================================================================
// A partir del reporte mensual por plataforma (`platformMonthlyReport`, en
// moneda base) arma lo que muestran las gráficas del encabezado:
//
//   · lo pagado en el año hasta hoy, por plataforma y en total
//   · la tendencia mes a mes y por año
//   · el comparativo contra los años anteriores, mes por mes y al mismo corte
//
// Lógica pura: el front la calcula con el reporte que ya pide al API.
// ============================================================================

import { monthRange, round2, type PlatformReport } from "./index";

/** Años hacia atrás que piden las gráficas (además del año en curso). */
export const PLATFORM_SPEND_YEARS_BACK = 3;

export interface PlatformSpendYear {
  year: number;
  /** Pagado en todo el año (en el año en curso, lo que va). */
  total: number;
  /** Pagado de enero al mes en curso, para comparar años al mismo corte. */
  toDate: number;
  /** Pagado por mes, enero → diciembre (12 posiciones). */
  months: number[];
}

export interface PlatformSpendByPlatform {
  platformId: string;
  name: string;
  paid: number;
}

export interface PlatformSpendHistory {
  year: number;
  /** Mes en curso (1-12). */
  month: number;
  /** Años con datos, del más viejo al actual. Siempre incluye el actual y el anterior. */
  years: PlatformSpendYear[];
  /** Pagado mes a mes en los últimos `trendMonths` meses (hasta el mes en curso). */
  monthly: { period: string; total: number }[];
  /** Pagado en el año en curso hasta hoy. */
  ytd: number;
  /** Pagado el año anterior al mismo corte (null si no hay datos de ese año). */
  previousToDate: number | null;
  /** Cambio del año en curso contra el anterior al mismo corte (0.1 = +10 %). */
  ytdChange: number | null;
  /** Pagado en todo el rango del reporte. */
  allTime: number;
  /** Pagado en el año en curso por plataforma, de mayor a menor (solo las que pagaron algo). */
  byPlatformYtd: PlatformSpendByPlatform[];
  /** Cargos del rango sin tipo de cambio: no suman hasta tenerlo. */
  chargesWithoutRate: number;
}

/** Rango de meses que piden las gráficas: enero de hace `yearsBack` años → mes en curso. */
export function platformSpendRange(today: string, yearsBack = PLATFORM_SPEND_YEARS_BACK): { from: string; to: string } {
  const year = Number(today.slice(0, 4));
  return { from: `${year - yearsBack}-01`, to: today.slice(0, 7) };
}

export function platformSpendHistory(report: readonly PlatformReport[], today: string, trendMonths = 24): PlatformSpendHistory {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const current = today.slice(0, 7);

  const byPeriod = new Map<string, number>();
  const ytdByPlatform = new Map<string, PlatformSpendByPlatform>();
  let chargesWithoutRate = 0;
  for (const r of report) {
    for (const m of r.months) {
      if (m.period > current) continue;
      byPeriod.set(m.period, (byPeriod.get(m.period) || 0) + m.paid);
      chargesWithoutRate += m.withoutRate || 0;
      if (m.period.startsWith(`${year}-`) && m.paid) {
        const row = ytdByPlatform.get(r.platformId) || { platformId: r.platformId, name: r.name, paid: 0 };
        row.paid += m.paid;
        ytdByPlatform.set(r.platformId, row);
      }
    }
  }

  const periods = [...byPeriod.keys()].sort();
  const firstYearWithData = periods.find((p) => (byPeriod.get(p) || 0) > 0)?.slice(0, 4);
  const firstYear = Math.min(firstYearWithData ? Number(firstYearWithData) : year, year - 1);

  const years: PlatformSpendYear[] = [];
  for (let y = firstYear; y <= year; y++) {
    const months = Array.from({ length: 12 }, (_, i) => round2(byPeriod.get(`${y}-${String(i + 1).padStart(2, "0")}`) || 0));
    years.push({
      year: y,
      total: round2(months.reduce((s, v) => s + v, 0)),
      toDate: round2(months.slice(0, month).reduce((s, v) => s + v, 0)),
      months,
    });
  }

  const [ty, tm] = [year, month];
  const startIndex = ty * 12 + (tm - 1) - (trendMonths - 1);
  const trendFrom = `${Math.floor(startIndex / 12)}-${String((startIndex % 12) + 1).padStart(2, "0")}`;
  const monthly = monthRange(trendFrom, current).map((period) => ({ period, total: round2(byPeriod.get(period) || 0) }));

  const thisYear = years[years.length - 1];
  const lastYear = years[years.length - 2];
  // Sin ningún pago registrado el año anterior no hay contra qué comparar.
  const hadLastYear = lastYear && lastYear.total > 0;
  const previousToDate = hadLastYear ? lastYear.toDate : null;
  const ytdChange = previousToDate ? round2((thisYear.toDate - previousToDate) / previousToDate) : null;

  return {
    year,
    month,
    years,
    monthly,
    ytd: thisYear.toDate,
    previousToDate,
    ytdChange,
    allTime: round2([...byPeriod.values()].reduce((s, v) => s + v, 0)),
    byPlatformYtd: [...ytdByPlatform.values()].map((r) => ({ ...r, paid: round2(r.paid) })).sort((a, b) => b.paid - a.paid),
    chargesWithoutRate,
  };
}

/** Las `top` plataformas con más pago y el resto sumado como una sola barra ("otras"). */
export function topPlatformsWithOther(rows: readonly PlatformSpendByPlatform[], top = 8): { rows: PlatformSpendByPlatform[]; otherCount: number } {
  if (rows.length <= top + 1) return { rows: [...rows], otherCount: 0 };
  const head = rows.slice(0, top);
  const rest = rows.slice(top);
  return {
    rows: [...head, { platformId: "__other__", name: "", paid: round2(rest.reduce((s, r) => s + r.paid, 0)) }],
    otherCount: rest.length,
  };
}
